import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityLoanConverterService} from './services/entity-loan-converter.service';
import {EntityLoanService} from './services/entity-loan.service';
import {EntityLoanViewModel} from './view-models/entity-loan.view-model';
import {ModalService} from '@shared/services/modal.service';
import {AppParameterMap} from '../../config/app-parameter-map';
import {PARAM_ENTITY_ID} from '../../config/app-urls';
import {Router} from '@angular/router';
import {Keys} from '@shared/keys';
import {EntityLoanDetailsModalComponent} from './entity-loan-details-modal/entity-loan-details-modal.component';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {EntityLoanCardContainerComponent} from './entity-loan-card-container/entity-loan-card-container.component';

@Component({
    selector: 'app-entity-loans',
    templateUrl: './entity-loans.component.html',
    styleUrls: ['./entity-loans.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TableTemplateComponent, EntityLoanCardContainerComponent]
})
export class EntityLoansComponent implements OnInit, TableTemplateComponentHost {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly entityLoanService = inject(EntityLoanService);
  private readonly clientSideDataService = inject(ClientsideDataService<EntityLoanViewModel>);
  private readonly entityLoanConverterService = inject(EntityLoanConverterService);
  private readonly modalService = inject(ModalService);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);

  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('name');
  readonly sortOrder = signal(1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<EntityLoanViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });
  private readonly SEARCH_FIELDS: Keys<EntityLoanViewModel> = ['portfolioNumber', 'name', 'loanType'];

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Loans);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const params = AppParameterMap.from(this.router);
    const entityId = params.get(PARAM_ENTITY_ID);

    this.entityLoanService.getEntityLoans(entityId).subscribe(loans => {
      const entityLoanVMs = this.entityLoanConverterService.convert(entityId, loans);
      this.clientSideDataService.initialize(entityLoanVMs);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(entityLoanVMs.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(dataViewModel: DataViewModel): void {
     const entityLoanViewModel = dataViewModel as EntityLoanViewModel;
     this.modalService.openModal(EntityLoanDetailsModalComponent, entityLoanViewModel);
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Portfolio Number',
        dataProperty: 'portfolioNumber',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '15%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Name',
        dataProperty: 'name',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Type',
        dataProperty: 'loanType',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '8%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Currency',
        dataProperty: 'currency',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Maturity Date',
        dataProperty: 'maturityDate',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Interest Rate',
        dataProperty: 'interestRate',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '11%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Balance',
        dataProperty: 'balance',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.Loans, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()
    ));
  }

}
